import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Edit2, Save, X, Plus, Trash2, Upload,
  Truck, Shield, Award, Users, Home, Star, Cpu, Layers,
  MapPin, Calendar, Hash, ChevronDown, ChevronUp,
  CheckCircle2, Loader2, Camera, ArrowLeft,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const ACCENT   = "#FF4D00";
const GOLD     = "#F5C842";
const DARK     = "#0A0A0A";
const CARD     = "#161618";
const MID      = "#1C1C1E";
const SOFT     = "#222224";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
];

const EQUIPMENT_OPTIONS = ["Dry Van","Reefer","Flatbed","Step Deck","Tanker","Hotshot","Box Truck","Sprinter/Van","Lowboy","Power Only"];
const LANE_OPTIONS = ["Nationwide","Northeast","Southeast","Midwest","Southwest","West","OTR","Regional"];
const FLEET_SIZE_OPTIONS = ["1–10 Trucks","11–50 Trucks","51–100 Trucks","100–250 Trucks","250+ Trucks"];

const BADGES = [
  { key: "badge_eld_supported",     label: "ELD Friendly",       Icon: Cpu,       color: "#3B82F6" },
  { key: "badge_new_trucks",        label: "New Fleet (2025+)",   Icon: Truck,      color: "#10B981" },
  { key: "badge_old_mc",            label: "MC 10+ Years",        Icon: Award,      color: GOLD      },
  { key: "badge_large_fleet",       label: "100+ Trucks",         Icon: Layers,     color: "#8B5CF6" },
  { key: "badge_owner_op_friendly", label: "Completed Lease",     Icon: Star,       color: ACCENT    },
  { key: "badge_driver_friendly",   label: "No Forced Dispatch",  Icon: Users,      color: "#06B6D4" },
  { key: "badge_home_weekly",       label: "Home Weekly",         Icon: Home,       color: "#84CC16" },
  { key: "badge_safety_first",      label: "70Y+",                Icon: Shield,     color: "#F97316" },
];

function initials(name = "") {
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "CC";
}

function toggleArr(arr = [], val) {
  return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
}

// ── Single profile card (used in directory) ─────────────────────────────────
export function CarrierProfileCard({ profile, onClick }) {
  const active = BADGES.filter((b) => profile[b.key]);
  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: "0 8px 32px rgba(255,77,0,0.12)" }}
      onClick={onClick}
      className="cursor-pointer p-4 space-y-3 transition-all"
      style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        {/* Logo / Initials avatar — card view (read-only) */}
        {profile.logo_path ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/market/carrier/logo/${encodeURIComponent((profile.carrier_key || profile.carrier_name_display || "").toLowerCase())}`}
            alt="logo"
            className="w-12 h-12 shrink-0 object-contain"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <div
          className="w-12 h-12 shrink-0 items-center justify-center font-head text-lg font-black"
          style={{ background: "rgba(255,77,0,0.12)", border: "1px solid rgba(255,77,0,0.3)", color: ACCENT, display: profile.logo_path ? "none" : "flex" }}>
          {initials(profile.carrier_name_display)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-head font-black text-sm text-white truncate">{profile.carrier_name_display || "Unknown Carrier"}</p>
          <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
            {[profile.home_base_state, profile.fleet_size_label].filter(Boolean).join(" · ") || "Profile incomplete"}
          </p>
          {(profile.mc_number || profile.dot_number) && (
            <p className="text-[10px] mt-0.5 font-mono" style={{ color: "rgba(255,255,255,0.3)" }}>
              {profile.mc_number ? `MC# ${profile.mc_number}` : ""}{profile.mc_number && profile.dot_number ? " · " : ""}{profile.dot_number ? `DOT# ${profile.dot_number}` : ""}
            </p>
          )}
        </div>
        {profile.bid_count > 0 && (
          <span className="shrink-0 font-head text-[9px] uppercase tracking-[0.12em] px-2 py-0.5"
            style={{ background: "rgba(255,77,0,0.1)", color: ACCENT, border: "1px solid rgba(255,77,0,0.25)" }}>
            {profile.bid_count} bids
          </span>
        )}
      </div>
      {/* Badges */}
      {active.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {active.slice(0, 4).map(({ key, label, Icon, color }) => (
            <span key={key} className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-head uppercase tracking-[0.1em] font-bold"
              style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
              <Icon className="w-2.5 h-2.5" />{label}
            </span>
          ))}
          {active.length > 4 && (
            <span className="text-[9px] font-head px-2 py-0.5" style={{ color: "rgba(255,255,255,0.3)", border: "1px solid rgba(255,255,255,0.1)" }}>
              +{active.length - 4} more
            </span>
          )}
        </div>
      )}
      {/* Equipment */}
      {(profile.equipment_operated || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {(profile.equipment_operated || []).slice(0, 4).map((eq) => (
            <span key={eq} className="text-[9px] px-2 py-0.5 font-head" style={{ background: MID, color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.08)" }}>{eq}</span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ── Full profile view (modal/expanded) ──────────────────────────────────────
export function CarrierProfileView({ profile, onClose, token, isOwn, onEdited }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ ...profile });
  const [saving, setSaving]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef     = useRef();
  const logoFileRef = useRef();

  useEffect(() => { setForm({ ...profile }); }, [profile]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleBadge = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));
  const toggleEquip = (val) => setForm((f) => ({ ...f, equipment_operated: toggleArr(f.equipment_operated || [], val) }));
  const toggleLane  = (val) => setForm((f) => ({ ...f, lanes: toggleArr(f.lanes || [], val) }));

  async function save() {
    setSaving(true);
    try {
      await api.marketUpdateCarrierProfile(token, {
        carrier_name_display:  form.carrier_name_display,
        company_description:   form.company_description,
        mc_number:             form.mc_number,
        dot_number:            form.dot_number,
        years_in_business:     form.years_in_business ? Number(form.years_in_business) : null,
        fleet_size_label:      form.fleet_size_label,
        home_base_state:       form.home_base_state,
        equipment_operated:    form.equipment_operated || [],
        lanes:                 form.lanes || [],
        badge_eld_supported:       form.badge_eld_supported || false,
        badge_new_trucks:          form.badge_new_trucks || false,
        badge_old_mc:              form.badge_old_mc || false,
        badge_large_fleet:         form.badge_large_fleet || false,
        badge_owner_op_friendly:   form.badge_owner_op_friendly || false,
        badge_driver_friendly:     form.badge_driver_friendly || false,
        badge_home_weekly:         form.badge_home_weekly || false,
        badge_safety_first:        form.badge_safety_first || false,
      });
      toast.success("Profile saved");
      setEditing(false);
      onEdited?.();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  async function uploadPhoto(file) {
    if (!file) return;
    setUploading(true);
    try {
      await api.marketUploadCarrierFleetPhoto(token, file);
      toast.success("Photo uploaded");
      onEdited?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }

  async function uploadLogo(file) {
    if (!file) return;
    setLogoUploading(true);
    try {
      await api.marketUploadCarrierLogo(token, file);
      toast.success("Company logo updated");
      onEdited?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally { setLogoUploading(false); }
  }

  async function deletePhoto(photoId) {
    try {
      await api.marketDeleteCarrierFleetPhoto(token, photoId);
      toast.success("Photo removed");
      onEdited?.();
    } catch { toast.error("Delete failed"); }
  }

  const display  = editing ? form : profile;
  const activeBadges = BADGES.filter((b) => display[b.key]);
  const fleetPhotos  = profile.fleet_photo_ids || [];

  const inputCls = "w-full text-sm px-3 py-2 focus:outline-none transition-colors";
  const inputStyle = { background: "#111", border: "1px solid #2a2a2a", color: "white" };
  const inputFocus = { borderColor: ACCENT };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        style={{ background: DARK, border: "1px solid rgba(255,255,255,0.08)" }}
      >
        {/* Cover */}
        <div className="relative h-28 overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0f0f0f 0%, #1a0a00 50%, #0f0f0f 100%)" }}>
          <div className="absolute inset-0 opacity-30"
            style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,77,0,0.05) 20px, rgba(255,77,0,0.05) 21px)" }} />
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-white/10 transition-colors" style={{ color: "rgba(255,255,255,0.5)" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Profile header */}
        <div className="px-6 pb-0 -mt-8 flex items-end justify-between">
          <div className="flex items-end gap-4">
            {/* ── Logo / Initials avatar with upload ── */}
            <div className="relative w-16 h-16 shrink-0 group">
              {profile.logo_path ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/market/carrier/logo/${encodeURIComponent((profile.carrier_key || "").toLowerCase())}`}
                  alt="Company logo"
                  className="w-16 h-16 object-contain"
                  style={{ background: DARK, border: `3px solid ${ACCENT}` }}
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                />
              ) : null}
              <div
                className="w-16 h-16 items-center justify-center font-head text-xl font-black"
                style={{ background: DARK, border: `3px solid ${ACCENT}`, color: ACCENT, display: profile.logo_path ? "none" : "flex" }}>
                {initials(display.carrier_name_display)}
              </div>
              {/* Upload overlay — only for profile owner */}
              {isOwn && (
                <>
                  <button
                    type="button"
                    data-testid="carrier-logo-upload-btn"
                    onClick={() => logoFileRef.current?.click()}
                    disabled={logoUploading}
                    className="absolute inset-0 w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                    style={{ background: "rgba(0,0,0,0.65)", border: `3px solid ${ACCENT}` }}
                    title="Upload company logo"
                  >
                    {logoUploading
                      ? <Loader2 className="w-5 h-5 animate-spin text-white" />
                      : <Camera className="w-5 h-5 text-white" />
                    }
                  </button>
                  <input
                    ref={logoFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
                  />
                </>
              )}
            </div>
            <div className="pb-2">
              {editing ? (
                <input
                  data-testid="carrier-display-name-input"
                  value={form.carrier_name_display || ""}
                  onChange={setF("carrier_name_display")}
                  placeholder="Company display name"
                  className={inputCls + " font-head font-black text-white text-lg"}
                  style={{ ...inputStyle, marginBottom: 2 }}
                />
              ) : (
                <h2 className="font-head text-lg font-black text-white">{display.carrier_name_display || "Your Company"}</h2>
              )}
              <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                {[display.home_base_state && `Based in ${display.home_base_state}`,
                  display.years_in_business && `${display.years_in_business} yrs in business`,
                  display.fleet_size_label
                ].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
          {isOwn && (
            <div className="flex gap-2 pb-2">
              {editing ? (
                <>
                  <button onClick={() => { setEditing(false); setForm({ ...profile }); }}
                    className="px-3 py-1.5 text-xs font-head uppercase tracking-[0.12em] transition-all"
                    style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
                    Cancel
                  </button>
                  <button onClick={save} disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-head uppercase tracking-[0.12em] font-black transition-all disabled:opacity-50"
                    style={{ background: ACCENT, color: "white" }}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Save
                  </button>
                </>
              ) : (
                <button onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-head uppercase tracking-[0.12em] font-black transition-all hover:brightness-110"
                  style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.15)" }}>
                  <Edit2 className="w-3 h-3" /> Edit Profile
                </button>
              )}
            </div>
          )}
        </div>

        <div className="px-6 pt-4 pb-8 space-y-6">
          {/* MC / DOT */}
          {(editing || display.mc_number || display.dot_number) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>MC Number</p>
                {editing
                  ? <input value={form.mc_number || ""} onChange={setF("mc_number")} placeholder="e.g. 123456"
                      className={inputCls} style={inputStyle} />
                  : <p className="text-sm font-mono" style={{ color: display.mc_number ? "white" : "rgba(255,255,255,0.2)" }}>
                      {display.mc_number ? `MC# ${display.mc_number}` : "—"}
                    </p>
                }
              </div>
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>DOT Number</p>
                {editing
                  ? <input value={form.dot_number || ""} onChange={setF("dot_number")} placeholder="e.g. 789012"
                      className={inputCls} style={inputStyle} />
                  : <p className="text-sm font-mono" style={{ color: display.dot_number ? "white" : "rgba(255,255,255,0.2)" }}>
                      {display.dot_number ? `DOT# ${display.dot_number}` : "—"}
                    </p>
                }
              </div>
            </div>
          )}

          {/* About */}
          {(editing || display.company_description) && (
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-2" style={{ color: ACCENT }}>About</p>
              {editing
                ? <textarea value={form.company_description || ""} onChange={setF("company_description")}
                    rows={4} placeholder="Tell drivers about your company, culture, what makes you stand out..."
                    className="w-full text-sm px-3 py-2.5 resize-none focus:outline-none"
                    style={{ background: "#111", border: "1px solid #2a2a2a", color: "white" }} />
                : <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>{display.company_description}</p>
              }
            </div>
          )}

          {/* Company details row */}
          {editing && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>Home State</p>
                <select value={form.home_base_state || ""} onChange={setF("home_base_state")}
                  className="w-full text-sm px-3 py-2 focus:outline-none appearance-none"
                  style={{ background: "#111", border: "1px solid #2a2a2a", color: "white" }}>
                  <option value="">— Select</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>Years in Business</p>
                <input type="number" min="1" max="80" value={form.years_in_business || ""} onChange={setF("years_in_business")}
                  placeholder="e.g. 12" className={inputCls} style={inputStyle} />
              </div>
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>Fleet Size</p>
                <select value={form.fleet_size_label || ""} onChange={setF("fleet_size_label")}
                  className="w-full text-sm px-3 py-2 focus:outline-none appearance-none"
                  style={{ background: "#111", border: "1px solid #2a2a2a", color: "white" }}>
                  <option value="">— Select</option>
                  {FLEET_SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Badges */}
          <div>
            <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-2" style={{ color: ACCENT }}>
              {editing ? "Company Credentials" : "Credentials & Highlights"}
            </p>
            <div className="flex flex-wrap gap-2">
              {BADGES.map(({ key, label, Icon, color }) => {
                const active = editing ? form[key] : display[key];
                if (!editing && !active) return null;
                return (
                  <button key={key} type="button"
                    onClick={editing ? () => toggleBadge(key) : undefined}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-head font-bold uppercase tracking-[0.1em] transition-all ${editing ? "cursor-pointer hover:brightness-110" : "cursor-default"}`}
                    style={{
                      background: active ? `${color}20` : "rgba(255,255,255,0.03)",
                      color:      active ? color : "rgba(255,255,255,0.25)",
                      border:     `1px solid ${active ? color + "50" : "rgba(255,255,255,0.1)"}`,
                    }}>
                    <Icon className="w-3 h-3" />
                    {label}
                    {editing && active && <CheckCircle2 className="w-2.5 h-2.5 ml-0.5" />}
                  </button>
                );
              })}
              {!editing && activeBadges.length === 0 && (
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>No credentials set yet</p>
              )}
            </div>
          </div>

          {/* Equipment */}
          <div>
            <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-2" style={{ color: ACCENT }}>Equipment Operated</p>
            <div className="flex flex-wrap gap-2">
              {(editing ? EQUIPMENT_OPTIONS : display.equipment_operated || []).map((eq) => {
                const selected = editing ? (form.equipment_operated || []).includes(eq) : true;
                if (!editing && !selected) return null;
                return (
                  <button key={eq} type="button"
                    onClick={editing ? () => toggleEquip(eq) : undefined}
                    className={`px-3 py-1.5 text-xs font-head uppercase tracking-[0.1em] transition-all ${editing ? "cursor-pointer" : "cursor-default"}`}
                    style={{
                      background: selected ? "rgba(255,77,0,0.1)" : "rgba(255,255,255,0.03)",
                      color:      selected ? ACCENT : "rgba(255,255,255,0.3)",
                      border:     `1px solid ${selected ? "rgba(255,77,0,0.35)" : "rgba(255,255,255,0.1)"}`,
                    }}>
                    {eq}
                  </button>
                );
              })}
              {!editing && (display.equipment_operated || []).length === 0 && (
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>Not specified</p>
              )}
            </div>
          </div>

          {/* Lanes */}
          <div>
            <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-2" style={{ color: ACCENT }}>Lanes Operated</p>
            <div className="flex flex-wrap gap-2">
              {(editing ? LANE_OPTIONS : display.lanes || []).map((lane) => {
                const selected = editing ? (form.lanes || []).includes(lane) : true;
                if (!editing && !selected) return null;
                return (
                  <button key={lane} type="button"
                    onClick={editing ? () => toggleLane(lane) : undefined}
                    className={`px-3 py-1.5 text-xs font-head uppercase tracking-[0.1em] transition-all ${editing ? "cursor-pointer" : "cursor-default"}`}
                    style={{
                      background: selected ? "rgba(245,200,66,0.08)" : "rgba(255,255,255,0.03)",
                      color:      selected ? GOLD : "rgba(255,255,255,0.3)",
                      border:     `1px solid ${selected ? "rgba(245,200,66,0.3)" : "rgba(255,255,255,0.1)"}`,
                    }}>
                    {lane}
                  </button>
                );
              })}
              {!editing && (display.lanes || []).length === 0 && (
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>Not specified</p>
              )}
            </div>
          </div>

          {/* Fleet Photos */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="font-head text-[9px] uppercase tracking-[0.2em]" style={{ color: ACCENT }}>Fleet Photos</p>
              {isOwn && (
                <>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-1 text-xs font-head uppercase tracking-[0.1em] transition-all disabled:opacity-50"
                    style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                    {uploading ? "Uploading..." : "Add Photo"}
                  </button>
                </>
              )}
            </div>
            {fleetPhotos.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {fleetPhotos.map((photoId) => (
                  <div key={photoId} className="relative group aspect-video overflow-hidden"
                    style={{ background: MID }}>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/market/carrier/fleet-photo/${profile.carrier_key}/${photoId}`}
                      alt="Fleet"
                      className="w-full h-full object-cover"
                    />
                    {isOwn && (
                      <button onClick={() => deletePhoto(photoId)}
                        className="absolute top-1 right-1 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: "rgba(0,0,0,0.7)", color: "#ef4444" }}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-8 text-center"
                style={{ border: "1px dashed rgba(255,255,255,0.1)" }}>
                <Camera className="w-8 h-8 mb-2 opacity-20" />
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>
                  {isOwn ? "Add fleet photos to show drivers your trucks" : "No fleet photos uploaded yet"}
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Carrier Directory ────────────────────────────────────────────────────────
export function CarrierDirectory({ token, selfCarrierName }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);

  async function load() {
    try {
      const r = await api.marketGetCarrierProfiles(token);
      setProfiles(r.profiles || []);
    } catch { toast.error("Failed to load carrier profiles"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: ACCENT }} />
    </div>
  );

  if (profiles.length === 0) return (
    <div className="flex flex-col items-center py-16 text-center">
      <Building2 className="w-12 h-12 mb-3 opacity-20" style={{ color: ACCENT }} />
      <p className="font-head text-sm text-white/30 uppercase tracking-[0.2em]">No carrier profiles yet</p>
      <p className="text-xs text-white/20 mt-1">Carriers who complete their profile will appear here.</p>
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {profiles.map((p) => (
          <CarrierProfileCard key={p.carrier_key} profile={p} onClick={() => setSelected(p)} />
        ))}
      </div>
      <AnimatePresence>
        {selected && (
          <CarrierProfileView
            profile={selected}
            token={token}
            isOwn={false}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ── Own Profile Panel (Full-Page LinkedIn-style) ─────────────────────────────
export function CarrierOwnProfile({ token, bids = [], carrierName = "" }) {
  const [profile, setProfile]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [editing, setEditing]         = useState(false);
  const [form, setForm]               = useState({});
  const [saving, setSaving]           = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef     = useRef();
  const logoFileRef = useRef();

  async function load() {
    setLoading(true);
    try {
      const p = await api.marketGetCarrierProfile(token);
      setProfile(p);
      setForm({ ...p });
    } catch { toast.error("Failed to load profile"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const setF  = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleBadge = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));
  const toggleEquip = (v) => setForm((f) => ({ ...f, equipment_operated: toggleArr(f.equipment_operated || [], v) }));
  const toggleLane  = (v) => setForm((f) => ({ ...f, lanes: toggleArr(f.lanes || [], v) }));

  async function save() {
    setSaving(true);
    try {
      await api.marketUpdateCarrierProfile(token, {
        carrier_name_display:   form.carrier_name_display,
        company_description:    form.company_description,
        mc_number:              form.mc_number,
        dot_number:             form.dot_number,
        years_in_business:      form.years_in_business ? Number(form.years_in_business) : null,
        fleet_size_label:       form.fleet_size_label,
        home_base_state:        form.home_base_state,
        equipment_operated:     form.equipment_operated || [],
        lanes:                  form.lanes || [],
        badge_eld_supported:        form.badge_eld_supported || false,
        badge_new_trucks:           form.badge_new_trucks || false,
        badge_old_mc:               form.badge_old_mc || false,
        badge_large_fleet:          form.badge_large_fleet || false,
        badge_owner_op_friendly:    form.badge_owner_op_friendly || false,
        badge_driver_friendly:      form.badge_driver_friendly || false,
        badge_home_weekly:          form.badge_home_weekly || false,
        badge_safety_first:         form.badge_safety_first || false,
      });
      toast.success("Profile saved");
      setEditing(false);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  async function uploadPhoto(file) {
    if (!file) return;
    setUploading(true);
    try { await api.marketUploadCarrierFleetPhoto(token, file); toast.success("Photo added"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  }

  async function uploadLogo(file) {
    if (!file) return;
    setLogoUploading(true);
    try { await api.marketUploadCarrierLogo(token, file); toast.success("Logo updated"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Logo upload failed"); }
    finally { setLogoUploading(false); }
  }

  async function deletePhoto(photoId) {
    try { await api.marketDeleteCarrierFleetPhoto(token, photoId); toast.success("Photo removed"); load(); }
    catch { toast.error("Delete failed"); }
  }

  // ── Stats derived from bids ────────────────────────────────────────────────
  const myBids    = bids.filter((b) => b.carrier_name === carrierName);
  const activeCt  = myBids.filter((b) => b.status === "live").length;
  const wonCt     = myBids.filter((b) => b.status === "transferred").length;
  const totalSpend = myBids.reduce((s, b) => s + (b.placement_fee || 0), 0);

  // ── Profile completion ─────────────────────────────────────────────────────
  function completion(p) {
    if (!p) return 0;
    const checks = [
      !!p.carrier_name_display,
      !!p.company_description,
      !!(p.mc_number || p.dot_number),
      !!p.home_base_state,
      !!p.fleet_size_label,
      (p.equipment_operated || []).length > 0,
      (p.lanes || []).length > 0,
      !!p.logo_path,
      (p.fleet_photo_ids || []).length > 0,
      BADGES.some((b) => p[b.key]),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }

  const d    = editing ? form : profile;
  const pct  = completion(profile);
  const pctColor = pct >= 80 ? "#22C55E" : pct >= 50 ? GOLD : ACCENT;

  const inputCls  = "w-full text-sm px-3 py-2 focus:outline-none transition-colors bg-[#111] text-white placeholder-white/25";
  const inputBdr  = { border: "1px solid #2a2a2a" };
  const focusBdr  = { borderColor: ACCENT };

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: ACCENT }} />
    </div>
  );

  const logoKey = encodeURIComponent(((profile?.carrier_key || "")).toLowerCase());
  const logoSrc = `${process.env.REACT_APP_BACKEND_URL}/api/market/carrier/logo/${logoKey}`;
  const activeBadges = BADGES.filter((b) => d?.[b.key]);

  return (
    <div className="space-y-0" data-testid="carrier-own-profile">

      {/* ── Cover Banner ──────────────────────────────────────────────────── */}
      <div className="relative h-40 sm:h-52 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0f0f0f 0%, #1c0800 45%, #0a0a0a 100%)" }}>
        {/* Grid texture */}
        <div className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,77,0,0.5) 39px, rgba(255,77,0,0.5) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,77,0,0.5) 39px, rgba(255,77,0,0.5) 40px)" }} />
        {/* Diagonal accent stripe */}
        <div className="absolute inset-0"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 60px, rgba(255,77,0,0.03) 60px, rgba(255,77,0,0.03) 61px)" }} />
        <div className="absolute bottom-4 left-6">
          <p className="font-head text-[9px] uppercase tracking-[0.5em] text-white/15">Hylas Companion · Carrier Hub</p>
        </div>
      </div>

      {/* ── Profile Header ────────────────────────────────────────────────── */}
      <div className="px-4 sm:px-6 -mt-10 pb-0"
        style={{ background: "#0A0A0A", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 pb-5">

          {/* Logo / Initials */}
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 group z-10">
            {profile?.logo_path ? (
              <img src={logoSrc} alt="Company logo"
                className="w-full h-full object-contain"
                style={{ background: "#0A0A0A", border: `4px solid #0A0A0A`, outline: `2px solid ${ACCENT}` }}
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
            ) : null}
            <div className="w-full h-full items-center justify-center font-head font-black text-2xl"
              style={{ background: "#161618", border: `4px solid #0A0A0A`, outline: `2px solid ${ACCENT}`, color: ACCENT,
                display: profile?.logo_path ? "none" : "flex" }}>
              {initials(d?.carrier_name_display || carrierName)}
            </div>
            {/* Upload overlay */}
            <button type="button" data-testid="carrier-logo-upload-btn"
              onClick={() => logoFileRef.current?.click()} disabled={logoUploading}
              className="absolute inset-0 w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "rgba(0,0,0,0.65)", outline: `2px solid ${ACCENT}` }}>
              {logoUploading ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Camera className="w-5 h-5 text-white" />}
            </button>
            <input ref={logoFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} />
          </div>

          {/* Name + tagline */}
          <div className="flex-1 pb-1 sm:pb-3 min-w-0">
            {editing ? (
              <input data-testid="carrier-display-name-input"
                value={form.carrier_name_display || ""} onChange={setF("carrier_name_display")}
                placeholder="Company display name"
                className="font-head font-black text-xl text-white w-full bg-transparent focus:outline-none pb-0.5"
                style={{ borderBottom: `1px solid ${ACCENT}` }} />
            ) : (
              <h1 className="font-head font-black text-xl sm:text-2xl text-white leading-tight truncate">
                {d?.carrier_name_display || carrierName || "Your Company"}
              </h1>
            )}
            <p className="text-xs mt-0.5 font-head uppercase tracking-[0.15em]" style={{ color: "rgba(255,255,255,0.35)" }}>
              {[d?.home_base_state && `Based in ${d.home_base_state}`,
                d?.years_in_business && `${d.years_in_business} yrs`,
                d?.fleet_size_label
              ].filter(Boolean).join(" · ") || "Complete your profile"}
            </p>
            {(d?.mc_number || d?.dot_number) && (
              <p className="text-[10px] font-mono mt-0.5" style={{ color: "rgba(255,255,255,0.2)" }}>
                {d.mc_number ? `MC#${d.mc_number}` : ""}{d.mc_number && d.dot_number ? " · " : ""}{d.dot_number ? `DOT#${d.dot_number}` : ""}
              </p>
            )}
          </div>

          {/* Edit / Save controls */}
          <div className="flex gap-2 pb-3 shrink-0">
            {editing ? (
              <>
                <button onClick={() => { setEditing(false); setForm({ ...profile }); }}
                  className="px-3 py-1.5 text-xs font-head uppercase tracking-[0.12em] transition-all"
                  style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
                  Cancel
                </button>
                <button onClick={save} disabled={saving} data-testid="carrier-profile-save-btn"
                  className="flex items-center gap-1.5 px-5 py-1.5 text-xs font-head uppercase tracking-[0.12em] font-black transition-all disabled:opacity-50 hover:brightness-110"
                  style={{ background: ACCENT, color: "white" }}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save
                </button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} data-testid="carrier-edit-profile-btn"
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-head uppercase tracking-[0.12em] font-black transition-all hover:brightness-110"
                style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.15)" }}>
                <Edit2 className="w-3 h-3" /> Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        {[
          { label: "Total Bids",     value: myBids.length, accent: false,      icon: <Truck className="w-3.5 h-3.5" /> },
          { label: "Active Bids",    value: activeCt,      accent: activeCt > 0,  icon: <Hash className="w-3.5 h-3.5" /> },
          { label: "Drivers Hired",  value: wonCt,         accent: wonCt > 0,    icon: <Users className="w-3.5 h-3.5" /> },
          { label: "Total Spend",    value: totalSpend > 0 ? `$${totalSpend.toLocaleString()}` : "$0",  accent: false, icon: <Star className="w-3.5 h-3.5" /> },
        ].map(({ label, value, accent: isAccent, icon }, i) => (
          <div key={i} className="flex flex-col items-center gap-0.5 py-3 px-2"
            style={{ background: "#0A0A0A", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
            <div className="flex items-center gap-1 mb-0.5" style={{ color: isAccent ? ACCENT : "rgba(255,255,255,0.2)" }}>{icon}</div>
            <p className="font-head text-base sm:text-lg font-black leading-none" style={{ color: isAccent ? ACCENT : "white" }}>{value}</p>
            <p className="font-head text-[8px] uppercase tracking-[0.15em] text-center" style={{ color: "rgba(255,255,255,0.25)" }}>{label}</p>
          </div>
        ))}
      </div>

      {/* ── Profile completion bar ────────────────────────────────────────── */}
      <div className="px-4 sm:px-6 py-3" style={{ background: "#0D0D0D", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-3">
          <p className="font-head text-[9px] uppercase tracking-[0.2em] shrink-0" style={{ color: "rgba(255,255,255,0.3)" }}>Profile</p>
          <div className="flex-1 h-1" style={{ background: "rgba(255,255,255,0.07)" }}>
            <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: pctColor }} />
          </div>
          <p className="font-head text-[10px] font-black shrink-0" style={{ color: pctColor }}>{pct}%</p>
          {pct < 100 && (
            <button onClick={() => setEditing(true)}
              className="font-head text-[9px] uppercase tracking-[0.15em] shrink-0 transition-colors hover:text-white"
              style={{ color: "rgba(255,255,255,0.3)" }}>
              {pct < 40 ? "Set up →" : "Complete →"}
            </button>
          )}
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-6 pt-6 px-4 sm:px-6 pb-10">

        {/* ─── Left column ─────────────────────────────────────────────── */}
        <div className="lg:col-span-8 space-y-6">

          {/* About */}
          <div className="p-5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-head text-[9px] uppercase tracking-[0.3em]" style={{ color: ACCENT }}>About</p>
              {!editing && <button onClick={() => setEditing(true)} className="text-white/20 hover:text-white/60 transition-colors"><Edit2 className="w-3 h-3" /></button>}
            </div>
            {editing ? (
              <textarea value={form.company_description || ""} onChange={setF("company_description")}
                rows={5} placeholder="Tell drivers about your company, culture, what makes you stand out as a great employer…"
                className="w-full text-sm px-3 py-2.5 resize-none focus:outline-none bg-[#111] text-white placeholder-white/25"
                style={{ border: "1px solid #2a2a2a" }} />
            ) : d?.company_description ? (
              <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>{d.company_description}</p>
            ) : (
              <button onClick={() => setEditing(true)}
                className="text-sm transition-colors hover:text-white/50" style={{ color: "rgba(255,255,255,0.2)" }}>
                + Add a company description…
              </button>
            )}
          </div>

          {/* Equipment operated */}
          <div className="p-5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
            <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-3" style={{ color: ACCENT }}>Equipment Operated</p>
            <div className="flex flex-wrap gap-2">
              {(editing ? EQUIPMENT_OPTIONS : d?.equipment_operated || []).map((eq) => {
                const sel = editing ? (form.equipment_operated || []).includes(eq) : true;
                if (!editing && !sel) return null;
                return (
                  <button key={eq} type="button" onClick={editing ? () => toggleEquip(eq) : undefined}
                    className={`px-3 py-1.5 text-xs font-head uppercase tracking-[0.1em] transition-all ${editing ? "cursor-pointer hover:brightness-110" : "cursor-default"}`}
                    style={{ background: sel ? "rgba(255,77,0,0.1)" : "rgba(255,255,255,0.03)", color: sel ? ACCENT : "rgba(255,255,255,0.3)", border: `1px solid ${sel ? "rgba(255,77,0,0.35)" : "rgba(255,255,255,0.1)"}` }}>
                    {eq}
                  </button>
                );
              })}
              {!editing && (d?.equipment_operated || []).length === 0 && (
                <button onClick={() => setEditing(true)} className="text-xs transition-colors hover:text-white/40" style={{ color: "rgba(255,255,255,0.2)" }}>+ Add equipment types</button>
              )}
            </div>
          </div>

          {/* Operating Lanes */}
          <div className="p-5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
            <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-3" style={{ color: GOLD }}>Operating Lanes</p>
            <div className="flex flex-wrap gap-2">
              {(editing ? LANE_OPTIONS : d?.lanes || []).map((lane) => {
                const sel = editing ? (form.lanes || []).includes(lane) : true;
                if (!editing && !sel) return null;
                return (
                  <button key={lane} type="button" onClick={editing ? () => toggleLane(lane) : undefined}
                    className={`px-3 py-1.5 text-xs font-head uppercase tracking-[0.1em] transition-all ${editing ? "cursor-pointer hover:brightness-110" : "cursor-default"}`}
                    style={{ background: sel ? "rgba(245,200,66,0.08)" : "rgba(255,255,255,0.03)", color: sel ? GOLD : "rgba(255,255,255,0.3)", border: `1px solid ${sel ? "rgba(245,200,66,0.3)" : "rgba(255,255,255,0.1)"}` }}>
                    {lane}
                  </button>
                );
              })}
              {!editing && (d?.lanes || []).length === 0 && (
                <button onClick={() => setEditing(true)} className="text-xs transition-colors hover:text-white/40" style={{ color: "rgba(255,255,255,0.2)" }}>+ Add operating lanes</button>
              )}
            </div>
          </div>

          {/* Fleet Photos */}
          <div className="p-5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-head text-[9px] uppercase tracking-[0.3em]" style={{ color: ACCENT }}>Fleet Photos</p>
              <>
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1 text-xs font-head uppercase tracking-[0.1em] transition-all disabled:opacity-50"
                  style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                  {uploading ? "Uploading…" : "Add Photo"}
                </button>
              </>
            </div>
            {(profile?.fleet_photo_ids || []).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {(profile.fleet_photo_ids || []).map((photoId) => (
                  <div key={photoId} className="relative group aspect-video overflow-hidden" style={{ background: MID }}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/market/carrier/fleet-photo/${profile.carrier_key}/${photoId}`}
                      alt="Fleet" className="w-full h-full object-cover" />
                    <button onClick={() => deletePhoto(photoId)}
                      className="absolute top-1 right-1 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: "rgba(0,0,0,0.7)", color: "#ef4444" }}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-8 text-center" style={{ border: "1px dashed rgba(255,255,255,0.08)" }}>
                <Camera className="w-8 h-8 mb-2 opacity-15" style={{ color: ACCENT }} />
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>Show drivers your trucks — upload fleet photos</p>
              </div>
            )}
          </div>
        </div>

        {/* ─── Right column ─────────────────────────────────────────────── */}
        <div className="lg:col-span-4 space-y-5 mt-6 lg:mt-0">

          {/* Credentials block */}
          <div className="p-5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
            <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-4" style={{ color: ACCENT }}>FMCSA Credentials</p>
            <div className="space-y-3">
              {[
                { label: "MC Number", field: "mc_number", prefix: "MC#", placeholder: "e.g. 948201" },
                { label: "DOT Number", field: "dot_number", prefix: "DOT#", placeholder: "e.g. 3829104" },
              ].map(({ label, field, prefix, placeholder }) => (
                <div key={field}>
                  <p className="font-head text-[8px] uppercase tracking-[0.2em] mb-1" style={{ color: "rgba(255,255,255,0.25)" }}>{label}</p>
                  {editing ? (
                    <input value={form[field] || ""} onChange={setF(field)} placeholder={placeholder}
                      className={inputCls + " font-mono text-xs"} style={inputBdr} />
                  ) : (
                    <p className="text-sm font-mono" style={{ color: d?.[field] ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.15)" }}>
                      {d?.[field] ? `${prefix}${d[field]}` : "—"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Company details */}
          <div className="p-5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
            <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-4" style={{ color: ACCENT }}>Company Details</p>
            <div className="space-y-3">
              <div>
                <p className="font-head text-[8px] uppercase tracking-[0.2em] mb-1" style={{ color: "rgba(255,255,255,0.25)" }}>Home State</p>
                {editing ? (
                  <select value={form.home_base_state || ""} onChange={setF("home_base_state")}
                    className="w-full text-sm px-3 py-2 focus:outline-none appearance-none bg-[#111] text-white" style={inputBdr}>
                    <option value="">— Select</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <p className="text-sm" style={{ color: d?.home_base_state ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.15)" }}>
                    {d?.home_base_state || "—"}
                  </p>
                )}
              </div>
              <div>
                <p className="font-head text-[8px] uppercase tracking-[0.2em] mb-1" style={{ color: "rgba(255,255,255,0.25)" }}>Years in Business</p>
                {editing ? (
                  <input type="number" min="1" max="80" value={form.years_in_business || ""} onChange={setF("years_in_business")}
                    placeholder="e.g. 12" className={inputCls} style={inputBdr} />
                ) : (
                  <p className="text-sm" style={{ color: d?.years_in_business ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.15)" }}>
                    {d?.years_in_business ? `${d.years_in_business} years` : "—"}
                  </p>
                )}
              </div>
              <div>
                <p className="font-head text-[8px] uppercase tracking-[0.2em] mb-1" style={{ color: "rgba(255,255,255,0.25)" }}>Fleet Size</p>
                {editing ? (
                  <select value={form.fleet_size_label || ""} onChange={setF("fleet_size_label")}
                    className="w-full text-sm px-3 py-2 focus:outline-none appearance-none bg-[#111] text-white" style={inputBdr}>
                    <option value="">— Select</option>
                    {FLEET_SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <p className="text-sm" style={{ color: d?.fleet_size_label ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.15)" }}>
                    {d?.fleet_size_label || "—"}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Highlights / Badges */}
          <div className="p-5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
            <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-3" style={{ color: ACCENT }}>
              {editing ? "Company Credentials" : "Highlights"}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {BADGES.map(({ key, label, Icon, color }) => {
                const active = editing ? form[key] : d?.[key];
                if (!editing && !active) return null;
                return (
                  <button key={key} type="button"
                    onClick={editing ? () => toggleBadge(key) : undefined}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-head font-bold uppercase tracking-[0.08em] transition-all ${editing ? "cursor-pointer hover:brightness-110" : "cursor-default"}`}
                    style={{ background: active ? `${color}18` : "rgba(255,255,255,0.03)", color: active ? color : "rgba(255,255,255,0.2)", border: `1px solid ${active ? color + "40" : "rgba(255,255,255,0.08)"}` }}>
                    <Icon className="w-2.5 h-2.5" />{label}
                    {editing && active && <CheckCircle2 className="w-2 h-2 ml-0.5" />}
                  </button>
                );
              })}
              {!editing && activeBadges.length === 0 && (
                <button onClick={() => setEditing(true)} className="text-xs transition-colors hover:text-white/40" style={{ color: "rgba(255,255,255,0.2)" }}>+ Add highlights</button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
